import { fileIcons } from '../../../../../consts/fileTypes';
import {
  TbDotsVertical,
  TbDownload,
  TbLock,
  TbLockOpen,
  TbReplace,
  TbTrash,
  TbFiles,
  TbFile
} from 'react-icons/tb';
import React, { useRef, useState } from 'react';
import { useUpdateUploadedFile } from '../../../../../services/period.service';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { serviceFundMapping } from '../../../../../services/funds.service';
import {
  Box,
  Button,
  Flex,
  Popover,
  Text,
  CloseButton,
  Tooltip,
  Progress,
  UnstyledButton
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useHover } from '@mantine/hooks';
import { createStyles } from '@mantine/emotion';
import { SPREADSHEET_FILE_ACCEPT } from '../../../../../consts/fileTypes';
import { extractApiError } from '../../../../../utils';
import { TailSpin } from 'react-loader-spinner';

const useStyles = createStyles(() => ({
  uploadType: {
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4
  }
}));

export const UploadedFundFiles = ({
  title,
  isLight,
  files,
  hideBadge,
  onDelete,
  onSelectProtectedFile,
  refetch,
  hideActions,
  isBulkUpload = false,
  acceptedFileTypes = SPREADSHEET_FILE_ACCEPT
}) => {
  return (
    <Flex direction="column" gap="12px">
      {files?.map(file => (
        <FundFileItem
          key={file.id}
          isProtected={file?.is_password_protected}
          isPasswordEntered={file?.has_password}
          isLight={!isLight}
          hideBadge={hideBadge}
          fileName={file?.name}
          fileType={file?.format}
          status={file?.status}
          onDelete={() => onDelete(file)}
          fileId={file.id}
          onSelectProtectedFile={() => onSelectProtectedFile(file)}
          refetch={refetch}
          hideActions={hideActions}
          isBulkUpload={isBulkUpload}
          acceptedFileTypes={acceptedFileTypes}
        />
      ))}
    </Flex>
  );
};

export const FundFileItem = ({
  fileName = 'Reviews.xlsx',
  onDelete,
  fileType = 'excel',
  isLight,
  isProtected,
  isPasswordEntered,
  onSelectProtectedFile,
  hideBadge,
  fileId,
  refetch,
  hideActions,
  isBulkUpload,
  status,
  acceptedFileTypes = SPREADSHEET_FILE_ACCEPT
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [searchParams] = useSearchParams();
  const fileRef = useRef();
  const { hovered, ref } = useHover();
  const Icon = fileIcons[fileType] || fileIcons.excel;
  const updateFile = useUpdateUploadedFile();

  const handleClickToLock = e => {
    e.preventDefault();
    e.stopPropagation();
    if (isPasswordEntered) return;

    onSelectProtectedFile();
  };
  const handleReplaceFile = () => {
    fileRef.current.click();
    onClose();
  };

  const handleDownload = () => {
    onDownload();
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  const onUpdateFile = e => {
    const { files } = e.target;

    const formData = new FormData();

    formData.append('file', files[0]);

    updateFile.mutate(
      {
        periodId: searchParams.get('periodId'),
        data: formData,
        fileId,
        onUploadProgress: data => {
          setProgress(Math.round((100 * data.loaded) / data.total));
        }
      },
      {
        onSuccess: res => {
          refetch();
          setProgress(0);
          toast.success('File replaced successfully');
        },
        onError: err => {
          setProgress(0);
          const detail = err?.data?.detail;

          if (
            err?.status === 415 &&
            typeof detail === 'string' &&
            detail.includes('Unsupported Media Type')
          ) {
            toast.error('Unsupported file format. Please upload an Excel or CSV file.');
          } else {
            toast.error(extractApiError(err, 'Unable to replace this file.'));
          }
        }
      }
    );
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const onDownload = async () => {
    try {
      notifications.show({
        loading: true,
        title: 'Downloading',
        position: 'bottom-center',
        autoClose: false
      });

      const response = await serviceFundMapping.downloadFile({
        periodId: searchParams.get('periodId'),
        fileId
      });

      const blob = new Blob([response]);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.log(e);
    } finally {
      notifications.clean();
    }
  };

  const { classes } = useStyles();

  const isNotCompleted = status !== 'completed';

  return (
    <Flex ref={ref} align="center" justify="space-between">
      {progress > 0 ? (
        <Box w="15%">
          <Progress radius="xs" size="sm" value={progress} />
        </Box>
      ) : (
        <>
          <input
            ref={fileRef}
            type="file"
            style={{ display: 'none' }}
            onChange={onUpdateFile}
            accept={acceptedFileTypes}
          />

          <Flex align="center" gap="8" opacity={isNotCompleted ? 0.5 : 1}>
            <Tooltip disabled={fileName.length <= 39} maw="220px" multiline label={fileName}>
              <Text truncate maw="260px" c="blue.8" size="sm">
                {fileName}
              </Text>
            </Tooltip>

            {isProtected && !isNotCompleted && (
              <CloseButton
                c={isPasswordEntered ? 'gray.6' : 'orange.6'}
                icon={isPasswordEntered ? <TbLockOpen /> : <TbLock />}
                onClick={handleClickToLock}
              />
            )}

            {isNotCompleted && <TailSpin width={10} height={10} strokeWidth={3} color="#228be6" />}

            {!hideBadge && !isNotCompleted && (
              <Tooltip label={isBulkUpload ? 'Multi-Upload' : 'Single-Upload'}>
                <Box className={classes.uploadType} bg="#F1F3F5">
                  {isBulkUpload ? (
                    <TbFiles size={16} color="#5C5F66" />
                  ) : (
                    <TbFile size={16} color="#5C5F66" />
                  )}
                </Box>
              </Tooltip>
            )}
          </Flex>
        </>
      )}

      {hovered && (
        <Flex align="center" w="100%" ml="4px">
          <Box
            miw="6px"
            h="6px"
            sx={() => ({
              borderRadius: '50%'
            })}
            bg="#D0EBFF"
          />
          <Box w="calc(100% - 20px)" flex="auto" bg="#D0EBFF" h="1px" />
          <Box
            miw="6px"
            h="6px"
            sx={() => ({
              borderRadius: '50%'
            })}
            bg="#D0EBFF"
          />
        </Flex>
      )}

      <Popover opened={isOpen} onChange={setIsOpen} position="bottom" shadow="md">
        {!isNotCompleted && (
          <Popover.Target>
            <UnstyledButton display="flex" onClick={() => setIsOpen(true)}>
              <TbDotsVertical color="#DEE2E6" fontSize="16px" />
            </UnstyledButton>
          </Popover.Target>
        )}
        <Popover.Dropdown>
          <Flex direction="column">
            {!hideActions && (
              <Button
                onClick={handleReplaceFile}
                size="xs"
                leftSection={<TbReplace fontSize="16px" />}
                variant="white"
                color="rgba(0, 0, 0, 1)"
                justify="left"
              >
                Replace File
              </Button>
            )}
            <Button
              onClick={handleDownload}
              size="xs"
              leftSection={<TbDownload fontSize="16px" />}
              variant="white"
              color="rgba(0, 0, 0, 1)"
              justify="left"
            >
              Download
            </Button>
            {!hideActions && (
              <Button
                onClick={handleDelete}
                justify="left"
                size="xs"
                leftSection={<TbTrash fontSize="16px" />}
                variant="white"
                color="red"
              >
                Delete
              </Button>
            )}
          </Flex>
        </Popover.Dropdown>
      </Popover>
    </Flex>
  );
};
